import React from 'react';
import { FacetConfiguredTermName } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { FacetConfig } from '../GovernanceAnalyticsAiVisibility.utils';
import { MAXIMUM_RADAR_COMPETITORS } from './attributeRadar.utils';

/**
 * Why the competitor average is not on offer, said out loud rather than left as an absence.
 *
 * A control that vanishes reads as a fault; the same control averaging a population of nobody reads
 * as a measurement. Neither is acceptable, so the row keeps its place and states the finding — that
 * these answers named no rival — which is itself the useful thing to know about them.
 */
const NO_COMPETITOR_POPULATION_NOTE = 'No competitor was measured in these answers, so there is no competitor average to compare against.';

/**
 * What the attribute radar is compared against.
 *
 * The control offers two mutually exclusive readings, not one flat list of toggles: comparing
 * against the FIELD (the competitor average) and comparing against NAMED rivals answer different
 * questions, and choosing either clears the other. That rule already lived in the state — "average"
 * is precisely the empty pin set — but the row read as a single sequence of equal options, so the
 * divider now says out loud what selecting either side already does.
 */
export function AttributeComparisonPicker({
  facetConfigs,
  competitorKeys,
  pinnedCompetitorKeys,
  onPinnedCompetitorKeysChange,
  competitorAverageAvailable,
}: {
  /**
   * The Competitor and CompetitorProduct term lists the page already fetched — read here instead of
   * through the shared term index, since those two facets are already loaded for the page's own
   * Breakdown/Gaps facets and asking the index for them again would be a second fetch of the same
   * terms.
   */
  facetConfigs: FacetConfig[];
  /** The rivals available to pin, already filtered to the ones the reader may see. */
  competitorKeys: readonly string[];
  pinnedCompetitorKeys: readonly string[];
  onPinnedCompetitorKeysChange: (next: string[]) => void;
  /**
   * Whether a competitor average was actually MEASURED for the filter in view.
   *
   * The condition is the data, never the prompt purpose. A brand-probe question names the brand and
   * no rival, so nothing rival-shaped is ever extracted from its answers and the average is taken
   * over an empty population — but keying on the purpose type would encode a proxy for that, and
   * the proxy is wrong in both directions: a custom purpose can produce no rivals either, and a
   * brand-probe answer that does name one should get the comparison back.
   */
  competitorAverageAvailable: boolean;
}) {
  const comparingAgainstField = !pinnedCompetitorKeys.length;

  return (
    <div className="flex flex-col items-center gap-1 mt-2">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-500">Compare with:</span>
        {competitorAverageAvailable ? (
          <button
            type="button"
            className={`text-[11px] rounded-md border px-2 py-0.5 ${
              comparingAgainstField ? 'border-sky-300 bg-sky-50 text-sky-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => onPinnedCompetitorKeysChange([])}
          >
            Competitor average
          </button>
        ) : null}

        {/* Only drawn when there are two groups to divide; a rule against nothing separates nothing. */}
        {competitorAverageAvailable && competitorKeys.length ? (
          <span className="h-3 w-px bg-slate-300" data-testid="comparison-group-divider" aria-hidden />
        ) : null}

        {competitorKeys.map((key) => {
          const pinned = pinnedCompetitorKeys.includes(key);
          return (
            <button
              key={`radar-pick:${key}`}
              type="button"
              className={`text-[11px] rounded-md border px-2 py-0.5 ${
                pinned ? 'border-sky-300 bg-sky-50 text-sky-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              // Capped at two rivals: a fourth polygon makes every overlap unreadable.
              onClick={() =>
                onPinnedCompetitorKeysChange(
                  pinned
                    ? pinnedCompetitorKeys.filter((entry) => entry !== key)
                    : [...pinnedCompetitorKeys, key].slice(-MAXIMUM_RADAR_COMPETITORS)
                )
              }
            >
              <FacetConfiguredTermName facetConfigs={facetConfigs} termKey={key} context="table-row" />
            </button>
          );
        })}
      </div>

      {competitorAverageAvailable ? null : <p className="text-[11px] text-slate-500 text-center mb-0">{NO_COMPETITOR_POPULATION_NOTE}</p>}
    </div>
  );
}
